/**
 * The model catalogue — the rows that identify a model rather than route to
 * one (PRD-0297 D-14).
 *
 * Its own API object rather than a member of `modelCapabilities`, because the
 * route family is its own: `/api/v1/models/` is mounted behind the viewer
 * floor alone and not behind the model-capability kill switch, since token
 * analytics resolves a reported model's context window through it on every
 * session-stats read. Folding these five calls into the governance object
 * would put a read that must always stand next to reads that need not.
 *
 * Every path carries the trailing slash the router declares, with one
 * exception the router also declares: `context-window` is registered without
 * one. FastAPI 307-redirects a mismatch before auth runs, and the client would
 * then follow an unauthenticated redirect into a 401, so each path here is
 * copied from the route rather than normalised.
 */

import type {
  CatalogueModel,
  CatalogueModelCreate,
  CatalogueModelFilters,
  CatalogueModelListResponse,
  CatalogueModelUpdate,
  ModelContextWindow,
} from '@/types/modelCapabilities'
import { apiClient } from './client'
import { ifMatchHeaders } from './ifMatch'

const BASE = '/api/v1/models'

export const modelCatalogue = {
  listModels: async (
    filters: CatalogueModelFilters = {}
  ): Promise<CatalogueModelListResponse> => {
    const response = await apiClient.get<CatalogueModelListResponse>(
      `${BASE}/`,
      { params: filters }
    )
    return response.data
  },

  // Keyed by the reported model *name*, not by a row id: the answer is which
  // pattern that name matches, which is a question about the catalogue's rules
  // rather than about any row the caller already holds.
  getContextWindow: async (modelName: string): Promise<ModelContextWindow> => {
    const response = await apiClient.get<ModelContextWindow>(
      `${BASE}/${encodeURIComponent(modelName)}/context-window`
    )
    return response.data
  },

  // 201. A pattern already in the catalogue is a 409 the shared error table
  // renders, not a merge: two rows claiming one pattern is what makes a
  // resolved context window ambiguous.
  createModel: async (body: CatalogueModelCreate): Promise<CatalogueModel> => {
    const response = await apiClient.post<CatalogueModel>(`${BASE}/`, body)
    return response.data
  },

  updateModel: async (
    modelId: string,
    body: CatalogueModelUpdate,
    ifMatch: number
  ): Promise<CatalogueModel> => {
    const response = await apiClient.patch<CatalogueModel>(
      `${BASE}/${modelId}/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  retireModel: async (
    modelId: string,
    ifMatch: number
  ): Promise<CatalogueModel> => {
    const response = await apiClient.post<CatalogueModel>(
      `${BASE}/${modelId}/retire/`,
      undefined,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },
}
